/**
 * Generates clock/time-telling questions for the Time Telling Challenge game.
 * No external API - all logic is local.
 */

/**
 * Format time as "H:MM" (e.g. 3:05, 12:30)
 */
export const formatTime = (hours, minutes) => {
    const h = hours % 12 || 12;
    const m = String(minutes).padStart(2, '0');
    return `${h}:${m}`;
};

/**
 * Generate a random time based on difficulty level.
 * @param {number} level - 1: hours only (0 min), 2: 5-minute steps, 3: any minute
 * @returns {{ hours: number, minutes: number }}
 */
const randomTimeForLevel = (level) => {
    const hours = Math.floor(Math.random() * 12) + 1;
    let minutes;
    if (level === 1) {
        minutes = 0;
    } else if (level === 2) {
        minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55][Math.floor(Math.random() * 12)];
    } else {
        minutes = Math.floor(Math.random() * 60);
    }
    return { hours, minutes };
};

/**
 * Generate distractor times that are different from the correct time.
 */
const generateDistractors = (correctHours, correctMinutes, level, count = 3) => {
    const correctStr = formatTime(correctHours, correctMinutes);
    const seen = new Set([correctStr]);
    const distractors = [];

    const offsetChoices = level === 1
        ? [1, 2, 3, 4, 5, 6, -1, -2, -3]
        : level === 2
            ? [15, 30, 45, 60, -15, -30, -45, 5, 10, 20, 25]
            : [5, 10, 15, 20, 25, 30, -5, -10, -15];

    while (distractors.length < count) {
        const offset = offsetChoices[Math.floor(Math.random() * offsetChoices.length)];
        let h = correctHours;
        let m = correctMinutes;

        if (level === 1) {
            h = ((h - 1 + offset + 12) % 12) + 1;
            m = 0;
        } else {
            let totalMins = h * 60 + m;
            totalMins += offset;
            if (totalMins < 0) totalMins += 12 * 60;
            h = Math.floor(totalMins / 60) % 12 || 12;
            m = totalMins % 60;
            if (level === 2) {
                m = Math.round(m / 5) * 5;
                if (m === 60) m = 0;
            }
        }

        const str = formatTime(h, m);
        if (!seen.has(str)) {
            seen.add(str);
            distractors.push(str);
        }
    }
    return distractors;
};

/**
 * Shuffle array in place (Fisher-Yates)
 */
const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

/**
 * Generate a clock MCQ question (Mode A: one clock, pick correct digital time).
 * @param {number} level - 1: hours only, 2: 5-minute steps, 3: any minute
 */
export const generateClockQuestion = (level = 2) => {
    const { hours, minutes } = randomTimeForLevel(level);
    const answer = formatTime(hours, minutes);
    const distractors = generateDistractors(hours, minutes, level);
    const options = shuffle([answer, ...distractors]);

    return {
        mode: 'read',
        hours,
        minutes,
        answer,
        options,
        type: 'clock',
    };
};

/**
 * Generate distractor clocks (different times) for match mode.
 */
const generateDistractorClocks = (correctHours, correctMinutes, level, count = 3) => {
    const correctStr = formatTime(correctHours, correctMinutes);
    const seen = new Set([correctStr]);
    const clocks = [];

    const offsetChoices = level === 1
        ? [1, 2, 3, 4, 5, -1, -2, -3]
        : level === 2
            ? [15, 30, 45, 60, -15, -30, 5, 10, 20, 25]
            : [10, 15, 20, 25, 30, -10, -15, -20];

    while (clocks.length < count) {
        const offset = offsetChoices[Math.floor(Math.random() * offsetChoices.length)];
        let h = correctHours;
        let m = correctMinutes;

        if (level === 1) {
            h = ((h - 1 + offset + 12) % 12) + 1;
            m = 0;
        } else {
            let totalMins = h * 60 + m;
            totalMins += offset;
            if (totalMins < 0) totalMins += 12 * 60;
            h = Math.floor(totalMins / 60) % 12 || 12;
            m = totalMins % 60;
            if (level === 2) m = Math.round(m / 5) * 5;
            if (m >= 60) m = 0;
        }

        const str = formatTime(h, m);
        if (!seen.has(str)) {
            seen.add(str);
            clocks.push({ hours: h, minutes: m });
        }
    }
    return clocks;
};

/**
 * Mode B: Given digital time, pick the correct clock from 4 options.
 */
export const generateClockMatchQuestion = (level = 2) => {
    const { hours, minutes } = randomTimeForLevel(level);
    const answer = formatTime(hours, minutes);
    const distractors = generateDistractorClocks(hours, minutes, level);

    const clockOptions = shuffle([
        { hours, minutes, isCorrect: true },
        ...distractors.map((d) => ({ ...d, isCorrect: false })),
    ]);

    const correctIndex = clockOptions.findIndex((c) => c.isCorrect);

    return {
        mode: 'match',
        targetHours: hours,
        targetMinutes: minutes,
        answer: String(correctIndex),
        clockOptions,
        type: 'clock',
    };
};

/**
 * Mode C: Set the clock hands to match the target time shown.
 */
export const generateClockSetQuestion = (level = 2) => {
    const { hours, minutes } = randomTimeForLevel(level);
    const answer = formatTime(hours, minutes);

    return {
        mode: 'set',
        targetHours: hours,
        targetMinutes: minutes,
        answer,
        type: 'clock',
    };
};

/**
 * Pick a random clock question mode and generate.
 */
export const generateRandomClockQuestion = (level = 2) => {
    const modes = ['read', 'match', 'set'];
    const mode = modes[Math.floor(Math.random() * modes.length)];
    if (mode === 'read') return generateClockQuestion(level);
    if (mode === 'match') return generateClockMatchQuestion(level);
    return generateClockSetQuestion(level);
};
